/**
 * usePOSDisplayEvents — SSE hook for POS to receive customer display actions
 * Listens for tip selections from the customer-facing display in real time.
 * Active only when the PaymentDialog is open.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { getTerminalId } from '../services/displayService';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function usePOSDisplayEvents(active = false) {
  const [customerTip, setCustomerTip] = useState(0);
  const [displayConnected, setDisplayConnected] = useState(false);
  const esRef = useRef(null);
  const reconnectRef = useRef(null);

  const reset = useCallback(() => {
    setCustomerTip(0);
    setDisplayConnected(false);
  }, []);

  useEffect(() => {
    if (!active) {
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
      clearTimeout(reconnectRef.current);
      reset();
      return;
    }

    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      const terminalId = getTerminalId();
      // EventSource cannot send headers: the cashier's session token rides as
      // ?token= (checked like the Bearer header the display pushes send).
      const token = localStorage.getItem('pos_token');
      if (!token) { setDisplayConnected(false); return; }
      const q = new URLSearchParams({ token });
      const es = new EventSource(`${API}/api/display/stream/${encodeURIComponent(terminalId)}?${q.toString()}`);
      esRef.current = es;

      es.addEventListener('connected', (e) => {
        try { JSON.parse(e.data); } catch { return; }
        setDisplayConnected(true);
      });

      es.addEventListener('TIP_SELECTED', (e) => {
        try {
          const d = JSON.parse(e.data);
          setCustomerTip(d.tip_amount || 0);
        } catch { /* ignore */ }
      });

      es.onopen = () => setDisplayConnected(true);
      es.onerror = () => {
        setDisplayConnected(false);
        es.close();
        if (!cancelled) {
          reconnectRef.current = setTimeout(connect, 3000);
        }
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
      clearTimeout(reconnectRef.current);
    };
  }, [active, reset]);

  return { customerTip, displayConnected, reset };
}
